import React, { useEffect, useState } from 'react';
import { Loader2, Mail, X } from 'lucide-react';
import { sendReportEmail } from './reportEmailApi';

function parseRecipients(raw) {
  return String(raw || '')
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Reusable modal to email report summaries across RCM services.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => object} props.buildPayload - returns API body for POST /reports/share/email
 * @param {string} [props.defaultSubject]
 * @param {string} [props.title]
 */
export default function ReportEmailDialog({
  open,
  onClose,
  buildPayload,
  defaultSubject = '',
  title = 'Email report',
}) {
  const [recipientsText, setRecipientsText] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject || '');
      setError(null);
      setSuccess(null);
    }
  }, [open, defaultSubject]);

  if (!open) return null;

  const handleSend = async () => {
    const recipients = parseRecipients(recipientsText);
    if (!recipients.length) {
      setError('Enter at least one email address.');
      return;
    }
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const base = buildPayload?.() || {};
      const result = await sendReportEmail({
        ...base,
        recipients,
        subject: subject.trim() || base.subject,
        message: message.trim() || null,
      });
      const failed = result.failed_recipients || [];
      if (failed.length) {
        setSuccess(`Sent to ${result.sent_count} recipient(s). Failed: ${failed.join(', ')}`);
      } else {
        setSuccess(`Report emailed to ${result.sent_count} recipient(s).`);
      }
    } catch (e) {
      setError(e.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            {title}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Recipients receive an HTML summary with key metrics and a link back to Reporting Center.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To (comma-separated)</label>
            <textarea
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              rows={2}
              placeholder="colleague@practice.com, cfo@practice.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Sharing our latest KPI snapshot for review before the board meeting."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{error}</div>
          )}
          {success && (
            <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">{success}</div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send email
          </button>
        </div>
      </div>
    </div>
  );
}
